import { useEffect, useState, useRef } from 'react'
import MediaCard from '../components/MediaCard'
import QRCodeGenerator from '../components/QRCodeGenerator'
import VoiceQRCodeGenerator from '../components/VoiceQRCodeGenerator'
import config from '../config'

// 来源标签映射
const SOURCE_LABEL = {
  media_search: '文字搜索',
  voice_page_search: '语音页面',
  chat: '对话',
}

const defaultVideoUrl = config.testVideoUrl

const withMediaUrl = (media) => ({
  ...media,
  url: media?.url || config.buildMediaUrl(media?.file_name),
})

const defaultMedias = [
  {
    name: '4K 2016首尔车展模型展示', director: '',
    actors: '', genres: '汽车',
    year: 2019, rating: 0, description: '超高清4K画质展示首尔车展上的精美汽车模型',
    file_name: '1.mp4', views: '8.2万', duration: '10:25',
    img: 'https://picsum.photos/640/360?random=1',
    url: defaultVideoUrl
  },
  {
    name: '8K 极致画质演示', director: '',
    actors: '', genres: '演示',
    year: 2023, rating: 0, description: '50fps流畅播放的8K超高清演示视频',
    file_name: '1.mp4', views: '15.6万', duration: '25:10',
    img: 'https://picsum.photos/640/360?random=2',
    url: defaultVideoUrl
  },
  {
    name: '4K 世界杯精彩集锦', director: '',
    actors: '', genres: '体育',
    year: 2023, rating: 0, description: '59.94fps高帧率世界杯比赛精彩瞬间',
    file_name: '1.mp4', views: '52.3万', duration: '15:42',
    img: 'https://picsum.photos/640/360?random=3',
    url: defaultVideoUrl
  },
  {
    name: '4K H.265高效编码演示', director: '',
    actors: '', genres: '技术',
    year: 2023, rating: 0, description: 'H.265编码技术展示，相同画质更小体积',
    file_name: '1.mp4', views: '4.1万', duration: '03:25',
    img: 'https://picsum.photos/640/360?random=4',
    url: defaultVideoUrl
  }
]

export default function Home({ ws, searchResults, chatHistory = [], onSearchResultsClear, onClearChatHistory }) {
  const [medias, setMedias] = useState(defaultMedias)
  const [displayTitle, setDisplayTitle] = useState('推荐视频')
  const [isSearchResult, setIsSearchResult] = useState(false)
  const [isChatOpen, setIsChatOpen] = useState(true)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [autoSpeak, setAutoSpeak] = useState(false)
  const [isMaleVoice, setIsMaleVoice] = useState(false)
  const [speed, setSpeed] = useState(1.0)
  const [activeQr, setActiveQr] = useState(null)
  
  const chatEndRef = useRef(null)
  const lastSpokenMsgIdRef = useRef(null)
  const speechSynthRef = useRef(null)
  const femaleVoiceRef = useRef(null)
  const maleVoiceRef = useRef(null)
  const voicesLoadedRef = useRef(false)

  // 初始化语音合成
  useEffect(() => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      speechSynthRef.current = window.speechSynthesis

      const loadVoices = () => {
        const voices = speechSynthRef.current.getVoices()
        const chineseVoices = voices.filter(voice =>
          voice.lang.includes('zh-CN') || voice.lang.includes('zh_CN')
        )

        let female = null
        let male = null
        if (chineseVoices.length > 0) {
          female = chineseVoices.find(voice =>
            voice.name.includes('Tingting') ||
            voice.name.includes('Yaoyao') ||
            voice.name.includes('Meijia') ||
            voice.name.toLowerCase().includes('female')
          ) || chineseVoices[0]

          male = chineseVoices.find(voice =>
            voice.name.includes('Kangkang') ||
            voice.name.includes('Yunyang') ||
            voice.name.toLowerCase().includes('male')
          )

          if (!male) {
            male = chineseVoices.find(v => v !== female)
          }
          if (!male) male = chineseVoices[0]
        } else if (voices.length > 0) {
          female = voices[0]
          male = voices[0]
        }
        femaleVoiceRef.current = female
        maleVoiceRef.current = male
        voicesLoadedRef.current = true
      }

      if (speechSynthRef.current.getVoices().length === 0) {
        speechSynthRef.current.onvoiceschanged = loadVoices
      } else {
        loadVoices()
      }
    }

    return () => {
      if (speechSynthRef.current) {
        speechSynthRef.current.cancel()
      }
    }
  }, [])

  // 自动播报新消息
  useEffect(() => {
    if (!autoSpeak) return
    if (!speechSynthRef.current) return
    if (!voicesLoadedRef.current) return
    if (chatHistory.length === 0) return

    const lastMsg = chatHistory[chatHistory.length - 1]

    if (lastMsg.role === 'assistant' && lastSpokenMsgIdRef.current !== lastMsg.id) {
      speakMessage(lastMsg.content)
      lastSpokenMsgIdRef.current = lastMsg.id
    }
  }, [chatHistory, autoSpeak])

  // 播报消息
  const speakMessage = (text) => {
    if (!speechSynthRef.current) return
    if (!voicesLoadedRef.current) return

    speechSynthRef.current.cancel()

    const cleanText = text
      .replace(/[#*_`]/g, '')
      .replace(/\s+/g, ' ')
      .trim()

    if (!cleanText) return

    const utterance = new SpeechSynthesisUtterance(cleanText)
    utterance.lang = 'zh-CN'
    utterance.rate = speed
    utterance.pitch = isMaleVoice ? 0.8 : 1.2
    utterance.volume = 1

    if (isMaleVoice && maleVoiceRef.current) {
      utterance.voice = maleVoiceRef.current
    } else if (!isMaleVoice && femaleVoiceRef.current) {
      utterance.voice = femaleVoiceRef.current
    }

    utterance.onStart = () => setIsSpeaking(true)
    utterance.onEnd = () => setIsSpeaking(false)
    utterance.onError = () => setIsSpeaking(false)

    speechSynthRef.current.speak(utterance)
  }

  const stopSpeaking = () => {
    if (speechSynthRef.current) {
      speechSynthRef.current.cancel()
      setIsSpeaking(false)
    }
  }

  const speakLastMessage = () => {
    if (chatHistory.length === 0) return
    const lastMsg = chatHistory[chatHistory.length - 1]
    speakMessage(lastMsg.content)
  }

  // 清除聊天记录
  const clearChatHistory = () => {
    if (onClearChatHistory) {
      onClearChatHistory()
    } else if (onSearchResultsClear) {
      onSearchResultsClear()
    }
    stopSpeaking()
  }

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ block: 'end' })
  }, [chatHistory])

  // 处理搜索结果
  useEffect(() => {
    if (!searchResults) return
    if (searchResults.type === 'chat') return

    let mediaCount = 0

    if (searchResults.medias_info && searchResults.medias_info.length > 0) {
      setMedias(searchResults.medias_info.map((media) => withMediaUrl(media)))
      setDisplayTitle(`搜索结果 (${searchResults.medias_info.length}部影片)`)
      setIsSearchResult(true)
      mediaCount = searchResults.medias_info.length
    } else if (searchResults.medias && searchResults.medias.length > 0) {
      setMedias(searchResults.medias.map((name, idx) => ({
        id: idx,
        name,
        description: '推荐电影',
        views: '-',
        duration: '-',
        url: defaultVideoUrl,
        file_name: '1.mp4',
        img: `https://picsum.photos/640/360?random=${idx}`
      })))
      setDisplayTitle(`搜索结果 (${searchResults.medias.length}部影片)`)
      setIsSearchResult(true)
      mediaCount = searchResults.medias.length
    } else {
      setMedias([])
      setDisplayTitle('搜索结果 (0部影片)')
      setIsSearchResult(true)
    }

    // 收到有效检索结果后，关闭当前打开的二维码弹窗，直接展示搜索结果
    if (searchResults.status === 'success' && mediaCount > 0) {
      setActiveQr(null)
    }

    setTimeout(() => {
      document.querySelector('.section-title')?.scrollIntoView({ block: 'start' })
    }, 300)
  }, [searchResults])

  const handleBackToRecommendations = () => {
    setMedias(defaultMedias)
    setDisplayTitle('推荐视频')
    setIsSearchResult(false)
    if (onSearchResultsClear) {
      onSearchResultsClear()
    }
  }

  const getSpeedLabel = () => {
    if (speed === 1.0) return '正常'
    if (speed === 1.5) return '较快'
    return '快速'
  }

  const toggleVoice = () => {
    setIsMaleVoice(!isMaleVoice)
    if (isSpeaking) {
      stopSpeaking()
    }
  }

  const chatToggleBorderColor = 'rgba(170, 197, 224, 0.9)'
  const chatPanelPadding = isChatOpen ? '18px' : '14px 8px'

  return (
    <div style={{ display: 'flex', gap: '0', minHeight: '100vh' }}>

      {/* 左侧主内容区 */}
      <div style={{ flex: 1, minWidth: 0 }} className="home-page">
        <div className="results-section">
          <h2 className="section-title" style={{ color: '#31404e' }}>
            {displayTitle}
          </h2>

          {isSearchResult && (
            <button
              className="back-button"
              onClick={handleBackToRecommendations}
              style={{
                width: '160px',
                alignSelf: 'flex-start',
                marginBottom: '20px', padding: '8px 16px',
                backgroundColor: '#f44336', color: 'white',
                border: 'none', borderRadius: '4px',
                cursor: 'pointer', fontSize: '14px'
              }}
            >
              ← 返回推荐视频
            </button>
          )}

          <div className="video-grid">
            {medias && medias.length > 0 ? (
              medias.map((media, i) => <MediaCard key={media.id ?? i} media={media} />)
            ) : (
              <div style={{ gridColumn: '1 / -1', padding: '40px 20px', textAlign: 'center', color: '#999' }}>
                {isSearchResult ? '未找到相关结果' : '加载推荐中...'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 右侧聊天记录面板 */}
      <div style={{
        width: isChatOpen ? '400px' : '64px',
        flexShrink: 0,
        background: isChatOpen ? '#fff' : 'linear-gradient(180deg, #ffffff, #f6fafe)',
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: '24px',
        alignSelf: 'flex-start',
        height: 'calc(100vh - 36px)',
        overflow: 'hidden',
        margin: '86px 12px 12px 0',
        borderRadius: '16px',
        border: isChatOpen ? '1px solid #eef2f6' : '1px solid #e3ebf3',
        boxShadow: isChatOpen ? '0 2px 10px rgba(2,6,23,0.03)' : '0 6px 18px rgba(15,23,42,0.06)',
        boxSizing: 'border-box',
      }}>
        {/* 标题栏 + 语音控制 */}
        <div style={{
          padding: chatPanelPadding,
          borderBottom: isChatOpen ? '1px solid #e0e0e0' : 'none',
          flexShrink: 0
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isChatOpen ? 'space-between' : 'center',
            marginBottom: isChatOpen ? '12px' : 0
          }}>
            {isChatOpen && (
              <span style={{ fontWeight: 600, fontSize: '14px', color: '#333' }}>
                💬 聊天记录
                {chatHistory.length > 0 && (
                  <span style={{
                    marginLeft: '6px', background: '#1976d2', color: '#fff',
                    borderRadius: '10px', padding: '1px 7px', fontSize: '12px'
                  }}>
                    {chatHistory.length}
                  </span>
                )}
              </span>
            )}
            <button
              onClick={() => setIsChatOpen(v => !v)}
              style={{
                background: 'linear-gradient(180deg, #f5f8fc, #edf3fa)',
                border: `1px solid ${chatToggleBorderColor}`,
                cursor: 'pointer',
                fontSize: isChatOpen ? '20px' : '18px',
                fontWeight: 700,
                color: '#4f6276',
                marginLeft: isChatOpen ? '0' : 'auto',
                marginRight: isChatOpen ? '0' : 'auto',
                width: isChatOpen ? '48px' : '30px',
                height: isChatOpen ? '36px' : '30px',
                minWidth: isChatOpen ? '48px' : '30px',
                borderRadius: '999px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: isChatOpen ? '0 3px 8px rgba(15,23,42,0.08)' : '0 2px 6px rgba(15,23,42,0.06)',
                lineHeight: 1,
                padding: 0,
              }}
            >
              {isChatOpen ? '›' : '‹'}
            </button>
          </div>

          {isChatOpen && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: '10px',
              fontSize: '12px'
            }}>
              <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                <div className="qr-container" style={{ flex: 1, minWidth: 0 }}>
                  <QRCodeGenerator
                    open={activeQr === 'upload'}
                    onOpenChange={(open) => setActiveQr(open ? 'upload' : null)}
                  />
                </div>
                <div className="qr-container" style={{ flex: 1, minWidth: 0 }}>
                  <VoiceQRCodeGenerator
                    open={activeQr === 'voice'}
                    onOpenChange={(open) => setActiveQr(open ? 'voice' : null)}
                  />
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap',
                fontSize: '12px'
              }}>
                {/* 自动播报开关 */}
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                  <span style={{ color: '#666' }}>🔊</span>
                  <input
                    type="checkbox"
                    checked={autoSpeak}
                    onChange={(e) => setAutoSpeak(e.target.checked)}
                    style={{ cursor: 'pointer', margin: 0 }}
                  />
                  <span style={{ color: '#666' }}>自动</span>
                </label>

                {/* 男声/女声切换 */}
                <button
                  onClick={toggleVoice}
                  style={{
                    padding: '2px 10px',
                    background: isMaleVoice ? '#2196f3' : '#e91e63',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  🎤 {isMaleVoice ? '男声' : '女声'}
                </button>

                {/* 语速切换 */}
                <button
                  onClick={() => setSpeed(speed === 1.0 ? 1.5 : speed === 1.5 ? 2.0 : 1.0)}
                  style={{
                    padding: '2px 10px',
                    background: '#ff9800',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    color: '#fff',
                    minWidth: '46px'
                  }}
                >
                  ⚡ {getSpeedLabel()}
                </button>

                {/* 播报和控制按钮组 */}
                <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
                  <button
                    onClick={speakLastMessage}
                    disabled={chatHistory.length === 0}
                    style={{
                      padding: '2px 10px',
                      background: chatHistory.length === 0 ? '#ccc' : '#1976d2',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: chatHistory.length === 0 ? 'not-allowed' : 'pointer',
                      fontSize: '11px',
                      color: '#fff'
                    }}
                  >
                    🔊 播报最新
                  </button>
                  {isSpeaking && (
                    <button
                      onClick={stopSpeaking}
                      style={{
                        padding: '2px 10px',
                        background: '#f44336',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        color: '#fff'
                      }}
                    >
                      ⏹️ 停止
                    </button>
                  )}
                  <button
                    onClick={clearChatHistory}
                    disabled={chatHistory.length === 0}
                    style={{
                      padding: '2px 10px',
                      background: chatHistory.length === 0 ? '#ccc' : '#666',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: chatHistory.length === 0 ? 'not-allowed' : 'pointer',
                      fontSize: '11px',
                      color: '#fff'
                    }}
                    title="清除聊天记录"
                  >
                    🗑️ 清空
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {!isChatOpen && (
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            padding: '10px 6px 18px'
          }}>
            <div style={{
              width: '34px',
              height: '34px',
              borderRadius: '12px',
              border: '1px solid #dce7f1',
              background: 'linear-gradient(180deg, #f5f8fc, #edf3fa)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              color: '#6b7d90',
              boxShadow: '0 2px 6px rgba(15,23,42,0.05)'
            }}>
              💬
            </div>
            {chatHistory.length > 0 && (
              <div style={{
                minWidth: '24px',
                padding: '2px 6px',
                borderRadius: '999px',
                background: '#1976d2',
                color: '#fff',
                fontSize: '11px',
                fontWeight: 700,
                textAlign: 'center'
              }}>
                {chatHistory.length}
              </div>
            )}
            <div style={{
              color: '#7a8ea4',
              fontSize: '11px',
              fontWeight: 700,
              lineHeight: 1.35,
              textAlign: 'center',
              whiteSpace: 'pre-line',
              letterSpacing: '0.08em',
              userSelect: 'none'
            }}>
              {'聊\n天\n记\n录'}
            </div>
          </div>
        )}

        {/* 消息列表 */}
        {isChatOpen && (
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '18px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            {chatHistory.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#aaa', fontSize: '13px', marginTop: '40px' }}>
                <div style={{ fontSize: '28px', marginBottom: '8px' }}>💬</div>
                暂无聊天记录
              </div>
            ) : (
              chatHistory.map(msg => (
                <div
                  key={msg.id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start'
                  }}
                >
                  <div style={{ fontSize: '11px', color: '#aaa', marginBottom: '3px' }}>
                    {msg.role === 'user' ? '🧑 ' : '🤖 '}
                    {SOURCE_LABEL[msg.source] || msg.source} · {msg.timestamp}
                    {autoSpeak && msg.role === 'assistant' && lastSpokenMsgIdRef.current === msg.id && isSpeaking && (
                      <span style={{ marginLeft: '6px', color: '#4caf50' }}>🔊</span>
                    )}
                  </div>
                  <div style={{
                    maxWidth: '85%',
                    padding: '8px 12px',
                    borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                    background: msg.role === 'user' ? '#1976d2' : '#fff',
                    color: msg.role === 'user' ? '#fff' : '#333',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    wordBreak: 'break-word'
                  }}>
                    {msg.content}
                    {msg.hasMedia && (
                      <div style={{
                        marginTop: '5px',
                        fontSize: '12px',
                        color: msg.role === 'user' ? 'rgba(255,255,255,0.8)' : '#1976d2'
                      }}>
                        🎬 已更新 {msg.mediaCount} 部影视
                      </div>
                    )}
                  </div>
                  {/* 每条消息的单独播报按钮 - 只对AI回复显示 */}
                  {msg.role === 'assistant' && (
                    <button
                      onClick={() => speakMessage(msg.content)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '10px',
                        color: '#999',
                        cursor: 'pointer',
                        marginTop: '4px',
                        padding: '2px 8px',
                        borderRadius: '12px'
                      }}
                      title="播报此消息"
                    >
                      🔊 单独播报
                    </button>
                  )}
                </div>
              
                ))
              )}
              <div ref={chatEndRef} />
            </div>
          )}
        
      </div>
    </div>
  )
}
